import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Package,
  Database,
  Code,
  Shield,
  Brain,
  Cloud,
  GraduationCap,
  VirtualReality,
  VideoCamera,
  ArrowUpRight,
  ArrowRight,
} from "@phosphor-icons/react";

import Hero from "@/components/Hero";
import SectionHeading from "@/components/SectionHeading";
import NewsletterSection from "@/components/NewsletterSection";
import {
  STATS,
  SERVICES,
  PRODUCTS,
  USE_CASES,
  CERTIFICATIONS,
  CLIENT_LOGOS,
} from "@/data/content";

const ICONS = {
  package: Package,
  database: Database,
  code: Code,
  shield: Shield,
  brain: Brain,
  cloud: Cloud,
  graduation: GraduationCap,
  vr: VirtualReality,
  camera: VideoCamera,
};

export default function Home() {
  return (
    <div data-testid="home-page" className="bg-white">
      <Hero />

      {/* Marquee */}
      <section className="border-y border-slate-200 bg-white py-4">
        <Marquee speed={40} gradient={false} pauseOnHover>
          {[...CERTIFICATIONS, ...CERTIFICATIONS].map((c, i) => (
            <span
              key={i}
              className="mx-8 font-mono text-xs tracking-[0.3em] uppercase text-slate-500"
            >
              {c} <span className="text-[#1D4ED8] mx-2">◆</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* Stats */}
      <section className="relative bg-white py-20 md:py-24" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-5 border-y border-slate-200 divide-x divide-slate-200">
            {STATS.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="px-4 py-8 md:py-10"
              >
                <div className="font-display text-4xl md:text-5xl font-black text-slate-900 tracking-tighter">
                  {s.value}
                </div>
                <div className="mt-2 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* About snippet */}
      <section className="relative bg-slate-50 py-20 md:py-32 border-y border-slate-200" data-testid="about-snippet">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <SectionHeading
              eyebrow="About Theta Dynamics"
              title={
                <>
                  Solutions across{" "}
                  <span className="text-[#1D4ED8]">Cyber Security, AI/ML, ERP, AR/VR and Surveillance.</span>
                </>
              }
              description="Theta Dynamics is a deep-tech engineering company building secure, intelligent and immersive systems. We design, ship and operate platforms for enterprises, governments and institutions — from threat-hunting consoles to AI-driven surveillance and city-scale ERP rollouts."
            />
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/about" className="btn-primary" data-testid="home-about-cta">
                More about us <ArrowUpRight size={16} weight="bold" />
              </Link>
              <Link to="/use-cases" className="btn-ghost" data-testid="home-cases-cta">
                See use cases
              </Link>
            </div>
          </div>

          <div className="md:col-span-5 grid grid-cols-2 gap-3">
            {[
              { v: "Security", l: "First" },
              { v: "AI/ML", l: "Native" },
              { v: "Odoo", l: "Partner" },
              { v: "AR/VR", l: "Studio" },
            ].map((b) => (
              <div key={b.v} className="card-flat p-5 md:p-6">
                <div className="font-display text-xl md:text-2xl font-bold text-slate-900">{b.v}</div>
                <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
                  {b.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="relative bg-white py-20 md:py-32" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <SectionHeading
              eyebrow="What we build"
              title="Services that span the digital stack."
              description="From Cyber Security and AI/ML to Odoo, ERPNext, AR/VR and Surveillance — an end-to-end engineering partner for ambitious teams."
            />
            <Link to="/services" className="btn-ghost shrink-0" data-testid="home-services-all">
              All services <ArrowRight size={16} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => {
              const Icon = ICONS[s.icon] || Code;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="card-flat p-7 md:p-9 group flex flex-col justify-between min-h-[240px]"
                >
                  <div>
                    <div className="flex items-center justify-between">
                      <Icon size={28} weight="duotone" className="text-[#1D4ED8]" />
                      <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
                        0{i + 1}
                      </span>
                    </div>
                    <h3 className="mt-5 font-display text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">
                      {s.name}
                    </h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed font-light">
                      {s.short}
                    </p>
                  </div>
                  <div className="mt-6 flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 group-hover:text-[#1D4ED8] transition-colors">
                    Explore <ArrowUpRight size={14} weight="bold" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="relative bg-slate-50 py-20 md:py-32 border-y border-slate-200" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <SectionHeading
              eyebrow="Our products"
              title="Software we build, own and operate."
              description="In addition to client engagements, Theta Dynamics ships its own products — used daily by lakhs of students and alumni across India."
            />
            <Link to="/products" className="btn-ghost shrink-0" data-testid="home-products-all">
              All products <ArrowRight size={16} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PRODUCTS.map((p) => (
              <Link
                key={p.slug}
                to={`/products/${p.slug}`}
                data-testid={`product-card-${p.slug}`}
                className="card-flat group overflow-hidden flex flex-col"
              >
                <div
                  className="h-56 bg-cover bg-center border-b border-slate-200 relative"
                  style={{ backgroundImage: `url(${p.image})` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-4 font-mono text-[10px] tracking-[0.22em] uppercase text-white drop-shadow">
                    Product
                  </div>
                </div>
                <div className="p-7 flex-1 flex flex-col">
                  <h3 className="font-display text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">
                    {p.name}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {p.tagline}
                  </p>
                  <div className="mt-auto pt-6 flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 group-hover:text-[#1D4ED8] transition-colors">
                    Learn more <ArrowUpRight size={14} weight="bold" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases preview */}
      <section className="relative bg-white py-20 md:py-32" data-testid="use-cases-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <SectionHeading
              eyebrow="Selected work"
              title="Real systems, in production, in the wild."
              description="From a Class-1 government contractor to NGOs across the Middle East — see how we modernise critical operations."
            />
            <Link to="/use-cases" className="btn-ghost shrink-0" data-testid="home-cases-all">
              All use cases <ArrowRight size={16} weight="bold" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
            {USE_CASES.slice(0, 3).map((c, i) => (
              <article
                key={c.client}
                data-testid={`home-case-${i}`}
                className={`card-flat p-7 md:p-9 ${
                  i === 0 ? "md:col-span-6" : "md:col-span-3"
                } flex flex-col`}
              >
                <div className="overline">{c.tags[0]}</div>
                <h3 className="mt-4 font-display text-xl md:text-2xl font-semibold text-slate-900 tracking-tight">
                  {c.client}
                </h3>
                <div className="mt-1 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500">
                  {c.sector}
                </div>
                <p className="mt-4 text-sm text-slate-600 leading-relaxed line-clamp-4">
                  {c.solution}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <NewsletterSection />

      {/* Clients marquee */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 mb-10">
          <SectionHeading
            eyebrow="Trusted by"
            title="Builders, governments and NGOs across 15+ countries."
            align="center"
          />
        </div>
        <Marquee speed={30} gradient={false} pauseOnHover>
          {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((c, i) => (
            <span
              key={i}
              className="mx-10 font-display text-2xl md:text-3xl font-bold text-slate-300 hover:text-[#1D4ED8] transition-colors"
            >
              {c}
            </span>
          ))}
        </Marquee>
      </section>

      {/* CTA */}
      <section className="relative bg-[#1D4ED8] overflow-hidden" data-testid="home-cta">
        <div className="absolute inset-0 grid-bg-dark opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="font-mono text-[11px] tracking-[0.28em] uppercase text-white/80 font-bold">Ready when you are</div>
            <h2 className="mt-4 font-display text-3xl md:text-5xl lg:text-6xl font-black text-white tracking-tighter leading-[1.05]">
              Tell us what you're building.
              <br />
              We'll engineer the rest.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/contact"
              data-testid="home-cta-button"
              className="btn-on-accent"
            >
              Get in touch <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
